import React, { useState, useEffect } from 'react';
import { FaTimes, FaMapMarkerAlt, FaCircle, FaToggleOn, FaToggleOff } from 'react-icons/fa';

interface State {
    id: number;
    name: string;
    is_active: boolean;
    created_by: number;
    suspended_by?: number | null;
    suspended_at?: string | null;
    created_at: string;
    updated_at: string;
}

interface EditStateFormData {
    id: number;
    name: string;
    is_active?: boolean;
}

interface EditStateOffcanvasProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: EditStateFormData) => void;
    state: State | null;
    loading?: boolean;
}

export const EditStateOffcanvas: React.FC<EditStateOffcanvasProps> = ({
    isOpen,
    onClose,
    onSubmit,
    state,
    loading = false
}) => {
    const [formData, setFormData] = useState<EditStateFormData>({
        id: 0,
        name: '',
        is_active: true
    });

    const [errors, setErrors] = useState<Partial<Omit<EditStateFormData, 'id' | 'is_active'>>>({});
    const [touched, setTouched] = useState<Record<string, boolean>>({
        name: false
    });

    // Update form data when state changes
    useEffect(() => {
        if (state && isOpen) {
            setFormData({
                id: state.id,
                name: state.name,
                is_active: state.is_active
            });
            setErrors({});
            setTouched({
                name: false
            });
        }
    }, [state, isOpen]);

    // Validation
    const validateField = (name: string, value: string) => {
        switch (name) {
            case 'name':
                if (!value.trim()) {
                    return 'State name is required';
                }
                if (value.trim().length < 2) {
                    return 'State name must be at least 2 characters long';
                }
                if (value.trim().length > 100) {
                    return 'State name must be less than 100 characters';
                }
                break;
        }
        return null;
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Validate field if it has been touched
        if (touched[name]) {
            const error = validateField(name, value);
            setErrors(prev => ({
                ...prev,
                [name]: error
            }));
        }
    };

    const handleToggleChange = () => {
        setFormData(prev => ({
            ...prev,
            is_active: !prev.is_active
        }));
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const { name } = e.target;

        setTouched(prev => ({
            ...prev,
            [name]: true
        }));

        // Validate field
        const error = validateField(name, formData[name as keyof typeof formData] as string || '');
        setErrors(prev => ({
            ...prev,
            [name]: error
        }));
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<Omit<EditStateFormData, 'id' | 'is_active'>> = {};
        let isValid = true;

        const fieldsToValidate = ['name'];
        fieldsToValidate.forEach(field => {
            const error = validateField(field, formData[field as keyof typeof formData] as string || '');
            if (error) {
                newErrors[field as keyof typeof newErrors] = error;
                isValid = false;
            }
        });

        setErrors(newErrors);
        setTouched({
            name: true
        });

        return isValid;
    };

    const hasChanges = (): boolean => {
        if (!state) return false;

        return (
            formData.name.trim() !== state.name ||
            formData.is_active !== state.is_active
        );
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        if (!hasChanges()) {
            onClose();
            return;
        }

        // Prepare data for submission
        const submitData: EditStateFormData = {
            id: formData.id,
            name: formData.name.trim(),
            is_active: formData.is_active
        };

        onSubmit(submitData);
    };

    const handleClose = () => {
        if (!loading) {
            onClose();
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (!isOpen || !state) return null;

    return (
        <>
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity" onClick={handleClose} />

            {/* Offcanvas */}
            <div className="fixed right-0 top-0 h-full w-full sm:max-w-2xl bg-white shadow-xl z-50 transform transition-transform overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-800">Edit State</h2>
                        <p className="text-sm text-gray-600 mt-1">Last updated: {formatDate(state.updated_at)}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-200 rounded transition-colors"
                        disabled={loading}
                    >
                        <FaTimes className="w-5 h-5 text-gray-600" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto">
                    <div className="px-6 py-6">
                        {/* State Info */}
                        <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-6">
                            <div className="flex items-center gap-3">
                                <div className="flex-shrink-0">
                                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                                        <FaMapMarkerAlt className="w-4 h-4 text-gray-600" />
                                    </div>
                                </div>
                                <div className="flex-1">
                                    <h4 className="text-sm font-medium text-gray-900">State ID: {state.id}</h4>
                                    <div className="text-xs text-gray-500 space-y-1">
                                        <div>Created: {formatDate(state.created_at)}</div>
                                        <div>Last Updated: {formatDate(state.updated_at)}</div>
                                        {state.suspended_at && (
                                            <div className="text-red-600">Suspended: {formatDate(state.suspended_at)}</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* State Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    State Name <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FaMapMarkerAlt className="w-4 h-4 text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        onBlur={handleBlur}
                                        placeholder="Enter state name"
                                        className={`block w-full pl-10 pr-3 py-3 border rounded shadow-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm ${errors.name && touched.name
                                            ? 'border-red-500 bg-red-50'
                                            : 'border-gray-300'
                                            }`}
                                        disabled={loading}
                                    />
                                </div>
                                {errors.name && touched.name && (
                                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-3">
                                    Status
                                </label>
                                <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-3 h-3 rounded-full ${formData.is_active ? 'bg-green-500' : 'bg-red-500'
                                            }`}></div>
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">
                                                {formData.is_active ? 'Active' : 'Inactive'}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {formData.is_active
                                                    ? 'State is currently active and available'
                                                    : 'State is currently suspended'
                                                }
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleToggleChange}
                                        disabled={loading}
                                        className={`p-2 rounded transition-colors ${formData.is_active
                                                ? 'text-green-600 hover:bg-green-50'
                                                : 'text-red-600 hover:bg-red-50'
                                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                                    >
                                        {formData.is_active ? (
                                            <FaToggleOn className="w-6 h-6" />
                                        ) : (
                                            <FaToggleOff className="w-6 h-6" />
                                        )}
                                    </button>
                                </div>
                            </div>

                            {/* Changes indicator */}
                            {hasChanges() && (
                                <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                                        <p className="text-sm text-yellow-800">
                                            You have unsaved changes
                                        </p>
                                    </div>
                                </div>
                            )}
                        </form>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                    <div className="flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={handleClose}
                            disabled={loading}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded  hover:bg-gray-50 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={loading || !formData.name.trim() || !hasChanges()}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-800 border border-transparent rounded hover:bg-blue-900 focus:ring-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    Updating...
                                </>
                            ) : (
                                <>
                                    <FaMapMarkerAlt className="w-4 h-4" />
                                    Update State
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};
